import { Frame, FrameStyle } from 'components/elements/Frame'
import MainTemplate from 'components/templates/MainTemplate'
import React from 'react'
import { StatesProps } from 'utils/commonHooks'
import { CLIENT_SETTINGS } from 'constants/clientSettings'
import Text from 'components/elements/texts/Text'
import Button, { ButtonStyle } from 'components/elements/buttons/Button'
import CallModal from 'components/elements/modal/CallModal'
import { getDomain, isSjDomain, isKsDomain } from 'utils/domain'
import DOMAIN from 'constants/domains'
import { transitionWithAction } from 'utils/commonUtils'
import { HistoryStackContext } from 'contexts/HistoryStackContext'
import { useHistory } from 'react-router-dom'
import { PAGE_PATH } from 'constants/pagePath'
import { QnStatesProps, initialQnEditState, QnState } from 'states/QnState'
import { useEditQnState, useClearEditingItem } from 'utils/commonHooks'
import { VEHICLE_STATE } from 'constants/qnState'
import { PageMessage } from 'constants/pageMessage'
import { QN_STATUS } from 'constants/qnStatus'

/** はじめから入力を開始するボタンのラベル */
const START_BUTTON_LABEL = 'はじめから入力する'
/** 離脱前の画面に戻るボタンのラベル */
const CONTINUE_BUTTON_LABEL = '続きから入力する'
/** 架電ボタンのラベル */
const HELP_BUTTON_LABEL = 'コールセンターに電話をかける'

/** 離脱前の画面を取得する */
const screenName: Record<string, string> = {
  // QN-02(スタート画面)
  '': PAGE_PATH.START,
  // QN-02-a(予約画面)
  日時を指定して呼ぶ: PAGE_PATH.RESERVATION,
  // QN-03(お車の状況画面)
  お車の状況: PAGE_PATH.VEHICLE_STATE,
  // QN-03-a ~ QN-03-h 各トラブル画面
  搬送先指定画面: PAGE_PATH.TRANSPORT_DESTINATION,
  [VEHICLE_STATE.ENGINE_TROUBLE]: PAGE_PATH.ENGINE_TROUBLE,
  [VEHICLE_STATE.FLAT_TIRE]: PAGE_PATH.FLAT_TIRE,
  [VEHICLE_STATE.ACCIDENT]: PAGE_PATH.ACCIDENT,
  [VEHICLE_STATE.FALLING_WHEEL]: PAGE_PATH.FALLING_WHEEL,
  [VEHICLE_STATE.EMPTY_FUEL]: PAGE_PATH.EMPTY_FUEL,
  [VEHICLE_STATE.LOSS_KEY]: PAGE_PATH.LOSS_KEY,
  [VEHICLE_STATE.OTHERS]: PAGE_PATH.OTHERS,
  // QN-04(車の場所選択画面)
  車の場所選択画面: PAGE_PATH.TROUBLE_PLACE,
  // QN-05(周辺状況画面)
  お車の周辺状況: PAGE_PATH.TROUBLE_PLACE_SURROUNDING,
  // SAIFC-01(宿・移サービス案内画面)
  [PageMessage['serviceInformation.title']]: PAGE_PATH.SERVICE_INFORMATION,
  // QN-06(契約情報入力画面)
  [PageMessage['contractInformation.title']]: PAGE_PATH.CONTRACT_INFORMATION,
  // QN-07(確認画面)
  ご確認ください: PAGE_PATH.CONFIRMATION,
}

/**
 * QN-00：復帰画面
 * @param props qnState
 * @returns
 */
const ReAccess: React.FC<StatesProps> = (props: QnStatesProps) => {
  const { state, setState } = props
  const [, setEditingItem] = useEditQnState()
  const history = useHistory()
  const historyStackContext = React.useContext(HistoryStackContext)
  const [historyContext, setHistoryContext] = historyStackContext
  const description: Array<string> =
    CLIENT_SETTINGS.messages.reAccess.description
  useClearEditingItem(true)

  /** SJクライアントかの判定フラグ */
  const isSJ = React.useMemo(() => getDomain().includes(DOMAIN.SJ), [])

  /** 共済系クライアントかの判定フラグ */
  const isKS = React.useMemo(() => isKsDomain(), [])

  /** Frameコンポーネントのスタイル定義 */
  const frameStyle: FrameStyle = React.useMemo(() => {
    return {
      border: `2px solid ${
        isKS ? CLIENT_SETTINGS.colors.main : isSJ ? '#cc0022' : '#ffffff'
      }`,
      padding: '12px',
    }
  }, [])

  /** ボタンのスタイル定義 */
  const buttonStyle: ButtonStyle = React.useMemo(() => {
    return {
      background: `${
        isKS ? CLIENT_SETTINGS.colors.main : isSJ ? '#cc0022' : '#EFEFF4'
      }`,
      color: `${isSJ || isKS ? '#FFFFFF' : '#F08200'}`,
    }
  }, [])

  /** 架電ボタンのスタイル定義 */
  const callButtonStyle: ButtonStyle = React.useMemo(() => {
    return {
      background: '#FFFFFF',
      color: `${
        isKS ? CLIENT_SETTINGS.colors.main : isSJ ? '#cc0022' : '#F08200'
      }`,
      bold: true,
      border: `3px solid ${
        isKS ? CLIENT_SETTINGS.colors.main : isSJ ? '#cc0022' : '#FFFFFF'
      }`,
    }
  }, [])

  /** はじめから入力するボタン押下時のクリックイベント */
  const onStartClick = React.useCallback((): void => {
    transitionWithAction(historyStackContext, START_BUTTON_LABEL, () => {
      /** 操作履歴の初期化 */
      setHistoryContext(historyContext.splice(0, 0))

      /** ローカルストレージの初期化 */
      const urlParameters = { ...state.urlParameters }

      // はじめから入力を選択しても以下の内容は保持する
      // ・URLのパラメータ(client, from, line)
      // ・A-2(着地URLアクセス機能)で取得した契約者情報
      setState({
        ...QnState,
        qnStatus: QN_STATUS.FILLING, // QnState.qnStatusの設定値は空文字が指定されているため、直接FILLINGを設定する
        urlParameters: {
          ...urlParameters,
        },
        contractInfo: {
          ...QnState.contractInfo,
          ...state.initialContractInfo,
        },
        initialContractInfo: state.initialContractInfo,
      })
      setEditingItem(
        state.initialContractInfo
          ? {
              ...initialQnEditState,
              contractInfo: {
                ...initialQnEditState.contractInfo,
                ...state.initialContractInfo,
              },
            }
          : initialQnEditState
      )

      /** QN-02(スタート画面)遷移 */
      history.push(PAGE_PATH.START)
      return
    })
  }, [state.urlParameters])

  /** 続きから入力するボタン押下時のクリックイベント */
  const onContinueClick = React.useCallback((): void => {
    transitionWithAction(historyStackContext, CONTINUE_BUTTON_LABEL, () => {
      /** 入力時の画面名を取得 */
      const previousPageName =
        historyContext.length > 1
          ? historyContext[historyContext.length - 2].page
          : ''

      /** 入力前の画面に遷移 */
      history.push(state.currentInputPagePath || screenName[previousPageName])
      return
    })
  }, [state.currentInputPagePath])

  return (
    <>
      <MainTemplate
        title=""
        content={
          <>
            <Frame {...frameStyle}>
              {description.map((message, i) => (
                <Text
                  key={i}
                  color={`${isSJ || isKS ? '#222222' : '#FFFFFF'}`}
                  bold
                >
                  {message}
                </Text>
              ))}
            </Frame>
            <Button
              {...buttonStyle}
              onClick={onStartClick}
              withArrow={isSjDomain() || isKsDomain()}
            >
              {START_BUTTON_LABEL}
            </Button>
            <Button
              {...buttonStyle}
              onClick={onContinueClick}
              withArrow={isSjDomain() || isKsDomain()}
            >
              {CONTINUE_BUTTON_LABEL}
            </Button>
            <div style={{ marginTop: '94px' }} />
            <Button {...callButtonStyle}>
              <CallModal isTransitionReAccess>{HELP_BUTTON_LABEL}</CallModal>
            </Button>
          </>
        }
        notClearEditingItem
      />
    </>
  )
}

export default ReAccess
